export interface QuickField {
  key: string;
  label: string;
  value: string;
}

export interface SectionContentBrief {
  title: string;
  instruction: string;
  example: string;
}

const QUICK_FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "heading", label: "Headline" },
  { key: "title", label: "Title" },
  { key: "headline", label: "Headline" },
  { key: "subheading", label: "Supporting copy" },
  { key: "description", label: "Description" },
  { key: "content", label: "Body copy" },
  { key: "text", label: "Text" },
  { key: "label", label: "Label" },
  { key: "buttonText", label: "Button text" },
  { key: "ctaLabel", label: "Call to action" },
];

const SECTION_GUIDANCE_RULES: Array<{
  match: RegExp;
  preferredKeys: string[];
  brief: SectionContentBrief;
}> = [
  {
    match: /hero|banner/i,
    preferredKeys: ["heading", "headline", "subheading", "buttonText", "ctaLabel"],
    brief: {
      title: "Hero brief",
      instruction: "State the main benefit in 6-10 words, support it in 1-2 short sentences, then use one clear CTA.",
      example: "Example: Book a cleaner smile in one visit. Same-week appointments, transparent pricing, and a care team that follows up fast.",
    },
  },
  {
    match: /about/i,
    preferredKeys: ["title", "heading", "content", "description", "text"],
    brief: {
      title: "About brief",
      instruction: "Explain who you help, why you exist, and why someone should trust you in 2-3 sentences.",
      example: "Example: We help busy families get preventive dental care without long waits. Our clinic combines flexible scheduling, clear treatment plans, and follow-up that feels personal.",
    },
  },
  {
    match: /feature|service/i,
    preferredKeys: ["heading", "title", "description", "content", "text"],
    brief: {
      title: "Services brief",
      instruction: "List the top 3-5 services or outcomes people buy first. Keep each one specific and easy to skim.",
      example: "Example: Dental implants, same-day crowns, preventive cleanings, emergency visits, and smile consultations.",
    },
  },
  {
    match: /pricing|plan/i,
    preferredKeys: ["heading", "title", "description", "content", "buttonText"],
    brief: {
      title: "Pricing brief",
      instruction: "Name each offer clearly, anchor it to the result, and remove uncertainty around what is included.",
      example: "Example: Whitening refresh, complete implant consult, and annual family care plan with transparent inclusions.",
    },
  },
  {
    match: /testimonial|review/i,
    preferredKeys: ["heading", "title", "description", "content", "text"],
    brief: {
      title: "Trust brief",
      instruction: "Use a short proof-oriented heading, then highlight one believable outcome or customer quote.",
      example: "Example: Why patients recommend us. ‘I booked on Tuesday, got treated on Thursday, and knew the cost before I sat down.’",
    },
  },
  {
    match: /faq/i,
    preferredKeys: ["heading", "title", "description", "content", "text"],
    brief: {
      title: "FAQ brief",
      instruction: "Answer the 3-5 questions that block conversion most often: price, timing, trust, and what happens next.",
      example: "Example: Do you offer payment plans? How fast can I book? What should I expect in the first visit?",
    },
  },
  {
    match: /cta|contact|form/i,
    preferredKeys: ["heading", "title", "subheading", "description", "buttonText", "ctaLabel"],
    brief: {
      title: "CTA brief",
      instruction: "Ask for one action only and make the next step explicit. Avoid generic button copy.",
      example: "Example: Get your treatment plan this week. Book your consultation.",
    },
  },
];

function isFilledString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function resolveGuidanceRule(componentType: string | null | undefined) {
  if (!componentType) {
    return null;
  }

  return SECTION_GUIDANCE_RULES.find((rule) => rule.match.test(componentType)) ?? null;
}

export function buildQuickFields(
  componentType: string | null | undefined,
  props: Record<string, unknown>,
): QuickField[] {
  const preferredKeys = resolveGuidanceRule(componentType)?.preferredKeys ?? [];
  const orderedFields = [
    ...preferredKeys,
    ...QUICK_FIELD_KEYS.map((field) => field.key),
  ];
  const seen = new Set<string>();

  return orderedFields.flatMap((key) => {
    if (seen.has(key)) {
      return [];
    }
    seen.add(key);

    const field = QUICK_FIELD_KEYS.find((entry) => entry.key === key);
    if (!field) {
      return [];
    }

    const value = props[field.key];
    if (typeof value === "string" || value === undefined) {
      return [{ key: field.key, label: field.label, value: typeof value === "string" ? value : "" }];
    }

    return [];
  }).slice(0, 3);
}

export function getSectionContentBrief(
  componentType: string | null | undefined,
  props: Record<string, unknown>,
): SectionContentBrief | null {
  const rule = resolveGuidanceRule(componentType);
  if (!rule) {
    return null;
  }

  const hasPrimaryCopy = rule.preferredKeys.some((key) => isFilledString(props[key]));
  return hasPrimaryCopy ? null : rule.brief;
}
